import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Input } from "antd";
import { LoadingOutlined, TrophyFilled } from "@ant-design/icons";
import AuthService from "../../../services/auth";
import DataProvider from "../../../services";
import GamesService from "../../../services/games";
import { REGISTRATION_PLATFORM_MCARE } from "../../../constants/registrationPlatform";
import { convertToInternationalFormat, convertToLocalFormat } from "../../../utils";
import { getAxiosErrorUserMessage } from "../../../utils/apiErrorMessage";
import { resetProfileCompletionGate } from "../../../lib/profile-completion";
import { useAppStore } from "../../../store";
import { MTN_YELLOW } from "../../../constants/Colors";

/**
 * Puts a finished run on the leaderboard, creating an account first if the
 * player is a guest — which is the point of the board (play as a guest, sign
 * up to be on it).
 *
 * The alias is asked for separately from the account name on purpose: the
 * board is a public page, so what goes on it is what the player chooses to be
 * called, not whatever is on their profile.
 */

/** Backend ConflictException text for a number that already has an account. */
const ALREADY_REGISTERED_RE = /already registered/i;

type Step = "alias" | "otp" | "existing-account" | "done";

export interface ScoreSubmitCardProps {
  game: string;
  score: number;
  bestSpan: number;
  rounds: number;
  onPosted?: (weeklyRank: number | null) => void;
}

const ScoreSubmitCard: React.FC<ScoreSubmitCardProps> = ({
  game,
  score,
  bestSpan,
  rounds,
  onPosted,
}) => {
  const { loggedIn, setToken, setUser } = useAppStore();
  const [step, setStep] = useState<Step>("alias");
  const [alias, setAlias] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [rank, setRank] = useState<number | null>(null);

  const intlPhone = () => convertToInternationalFormat(phoneNumber.trim());

  const post = async () => {
    const result = await GamesService.submitScore({
      game,
      alias: alias.trim(),
      score,
      bestSpan,
      rounds,
    });
    setRank(result.weeklyRank);
    setStep("done");
    onPosted?.(result.weeklyRank);
  };

  const handleAliasSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (alias.trim().length < 2) {
      setError("Pick a name for the board — at least 2 characters.");
      return;
    }
    setError(null);
    setBusy(true);
    try {
      if (loggedIn) {
        // Already has an account: straight onto the board.
        await post();
        return;
      }
      if (phoneNumber.trim().length < 9) {
        setError("Enter the phone number to create your account with.");
        return;
      }
      await AuthService.signUpPhone({
        phoneNumber: intlPhone(),
        // The account name doubles as the board name here: this flow asks for
        // one thing, not two, and the player can change their profile later.
        fullname: alias.trim(),
        registrationPlatform: REGISTRATION_PLATFORM_MCARE,
      });
      setStep("otp");
    } catch (err) {
      const message = getAxiosErrorUserMessage(err);
      if (ALREADY_REGISTERED_RE.test(message)) {
        setStep("existing-account");
        setError(null);
      } else {
        setError(message);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleOtpSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (otpCode.trim().length < 6) {
      setError("Enter the 6-digit code we sent you.");
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const response = await AuthService.signUpVerify({
        phoneNumber: intlPhone(),
        otpCode: otpCode.trim(),
        fullname: alias.trim() || undefined,
        registrationPlatform: REGISTRATION_PLATFORM_MCARE,
      });

      // Establish the session before posting: the score endpoint needs the
      // token the interceptor reads from storage.
      await DataProvider.setToken(response.token);
      await DataProvider.setRefreshToken(response.refreshToken);
      setToken({ token: response.token });
      resetProfileCompletionGate();

      const { data: profile } = await DataProvider.updateProfile({
        fullname: alias.trim(),
        phoneNumber: convertToLocalFormat(phoneNumber.trim()),
      });
      setUser({ ...profile, timenow: new Date() });

      await post();
    } catch (err) {
      const message = getAxiosErrorUserMessage(err);
      if (ALREADY_REGISTERED_RE.test(message)) {
        setStep("existing-account");
        setError(null);
      } else {
        setError(message);
      }
    } finally {
      setBusy(false);
    }
  };

  if (step === "done") {
    return (
      <div className="w-full rounded-xl bg-white/10 px-4 py-3 text-center">
        <p className="m-0 flex items-center justify-center gap-2 text-sm font-semibold text-white">
          <TrophyFilled style={{ color: MTN_YELLOW }} />
          You&apos;re on the board
          {rank ? ` — ${rank}${ordinal(rank)} this week` : ""}
        </p>
        <Link
          to="/play/leaderboard"
          className="mt-1 inline-block text-xs text-white/70 underline"
        >
          See the leaderboard
        </Link>
      </div>
    );
  }

  if (step === "existing-account") {
    return (
      <div className="w-full space-y-2 rounded-xl bg-white/10 px-4 py-3">
        <p className="m-0 text-xs leading-relaxed text-white/85">
          {phoneNumber.trim() || "That number"} already has an account. Log in
          and your score goes straight on the board.
        </p>
        <Link
          to="/sign-in"
          className="block w-full rounded-lg px-4 py-2 text-center text-sm font-semibold text-black"
          style={{ backgroundColor: MTN_YELLOW }}
        >
          Log in
        </Link>
        <button
          type="button"
          onClick={() => {
            setPhoneNumber("");
            setOtpCode("");
            setStep("alias");
          }}
          className="w-full text-xs text-white/50 underline"
        >
          Use a different number
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={step === "otp" ? handleOtpSubmit : handleAliasSubmit}
      className="w-full space-y-2 rounded-xl bg-white/10 px-4 py-3 text-left"
    >
      {step === "alias" ? (
        <>
          <p className="m-0 text-xs text-white/70">
            {loggedIn
              ? "Pick the name to show on the board."
              : "Pick a board name and we'll set up your account."}
          </p>
          <Input
            value={alias}
            onChange={(event) => setAlias(event.target.value)}
            placeholder="Board name, e.g. Kwame A."
            maxLength={20}
            size="large"
          />
          {!loggedIn && (
            <Input
              value={phoneNumber}
              onChange={(event) => setPhoneNumber(event.target.value)}
              placeholder="Phone number"
              inputMode="tel"
              size="large"
            />
          )}
        </>
      ) : (
        <>
          <p className="m-0 text-xs text-white/70">
            Enter the 6-digit code we sent to {phoneNumber.trim()}.
          </p>
          <Input
            value={otpCode}
            onChange={(event) => setOtpCode(event.target.value)}
            placeholder="6-digit code"
            inputMode="numeric"
            maxLength={6}
            size="large"
          />
        </>
      )}

      {error && <p className="m-0 text-xs text-red-300">{error}</p>}

      <button
        type="submit"
        disabled={busy}
        className="w-full rounded-lg px-4 py-2.5 text-sm font-semibold text-black transition-opacity disabled:opacity-60"
        style={{ backgroundColor: MTN_YELLOW }}
      >
        {busy ? <LoadingOutlined /> : null}
        {busy
          ? " Working…"
          : step === "otp"
            ? "Confirm and post my score"
            : loggedIn
              ? "Put me on the board"
              : "Create account and post"}
      </button>
      <p className="m-0 text-[10px] leading-relaxed text-white/40">
        The board shows your board name and score only.
      </p>
    </form>
  );
};

/** 1st, 2nd, 3rd, 4th — including the 11th/12th/13th exceptions. */
function ordinal(n: number): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return "th";
  switch (n % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
}

export default ScoreSubmitCard;
